import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { PageLoader } from '../components/LoadingSpinner';
import { CreatorBadges, CreatorBadgeSection } from '../components/VerifiedBadge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Store, MessageCircle, Calendar, Heart, Users, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StorePage() {
  const { id } = useParams();
  const { user, token } = useAuth();
  
  const [store, setStore] = useState(null);
  const [creator, setCreator] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endorsements, setEndorsements] = useState([]);
  const [hasEndorsed, setHasEndorsed] = useState(false);
  const [endorseDialogOpen, setEndorseDialogOpen] = useState(false);
  const [endorseMessage, setEndorseMessage] = useState('');
  const [endorsing, setEndorsing] = useState(false);
  
  // Shoutout state
  const [canShoutout, setCanShoutout] = useState(false);
  const [shoutoutInfo, setShoutoutInfo] = useState(null);
  const [shoutoutDialogOpen, setShoutoutDialogOpen] = useState(false);
  const [shoutoutMessage, setShoutoutMessage] = useState('');
  const [sendingShoutout, setSendingShoutout] = useState(false);

  useEffect(() => {
    fetchStore();
    fetchEndorsements();
    if (user) {
      checkEndorsement();
      checkCanShoutout();
    }
  }, [id, user]);

  const fetchStore = async () => {
    try {
      const res = await fetch(`${API}/stores/${id}`);
      if (res.ok) {
        const data = await res.json();
        setStore(data.store);
        setCreator(data.creator);
        setProducts(data.products || []);
      }
    } catch (e) {
      console.error('Error fetching store:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchEndorsements = async () => {
    try {
      const res = await fetch(`${API}/endorsements/${id}`);
      if (res.ok) {
        setEndorsements(await res.json());
      }
    } catch (e) {
      console.error('Error fetching endorsements:', e);
    }
  };

  const checkEndorsement = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/endorsements/check/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHasEndorsed(data.has_endorsed);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const checkCanShoutout = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/shoutouts/can-shoutout/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCanShoutout(data.can_shoutout);
        setShoutoutInfo(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleShoutout = async (e) => {
    e.preventDefault();
    if (!shoutoutMessage.trim()) {
      toast.error('Please write a welcome message');
      return;
    }
    
    setSendingShoutout(true);
    try {
      const res = await fetch(`${API}/shoutouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ store_id: id, message: shoutoutMessage })
      });
      if (res.ok) {
        toast.success('Shoutout sent! They will be so happy!');
        setShoutoutDialogOpen(false);
        setShoutoutMessage('');
        setCanShoutout(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send shoutout');
      }
    } catch (e) {
      toast.error('Error sending shoutout');
    } finally {
      setSendingShoutout(false);
    }
  };

  const handleEndorse = async (e) => {
    e.preventDefault();
    setEndorsing(true);
    try {
      const res = await fetch(`${API}/endorsements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ store_id: id, message: endorseMessage })
      });
      if (res.ok) {
        toast.success('Endorsement added!');
        setEndorseDialogOpen(false);
        setEndorseMessage('');
        setHasEndorsed(true);
        fetchStore();
        fetchEndorsements();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to endorse');
      }
    } catch (e) {
      toast.error('Error adding endorsement');
    } finally {
      setEndorsing(false);
    }
  };

  const handleRemoveEndorsement = async () => {
    try {
      const res = await fetch(`${API}/endorsements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Endorsement removed');
        setHasEndorsed(false);
        fetchStore();
        fetchEndorsements();
      }
    } catch (e) {
      toast.error('Error removing endorsement');
    }
  };

  if (loading) return <PageLoader />;
  if (!store) return <div className="text-center py-16">Store not found</div>;

  return (
    <div className="min-h-screen">
      {/* Banner */}
      <div className="relative h-48 md:h-64 bg-gradient-to-br from-pastel-crochet/50 via-pastel-stickers/50 to-pastel-art/50">
        {store.banner_image && (
          <img 
            src={store.banner_image} 
            alt={store.name}
            className="w-full h-full object-cover"
          />
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Store Header */}
        <div className="relative -mt-16 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start md:items-end">
            {/* Logo */}
            <div className="w-32 h-32 rounded-2xl border-4 border-card bg-card overflow-hidden shadow-brutal flex-shrink-0">
              {store.logo ? (
                <img src={store.logo} alt={store.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-pastel-keyrings/30">
                  <Store className="w-12 h-12" />
                </div>
              )}
            </div>

            <div className="flex-1 pt-4 md:pt-0">
              <h1 className="text-3xl md:text-4xl font-heading font-extrabold flex items-center gap-2" data-testid="store-name">
                {store.name}
                <CreatorBadges 
                  isVerified={store.is_verified}
                  isFoundingCreator={store.is_founding_creator}
                  tier={store.tier}
                  size="lg"
                />
              </h1>
              <p className="text-muted-foreground mt-1">{store.description}</p>
              
              {(store.is_founding_creator || store.is_verified || store.tier || store.endorsement_count > 0) && (
                <div className="mt-3">
                  <CreatorBadgeSection 
                    isVerified={store.is_verified}
                    isFoundingCreator={store.is_founding_creator}
                    tier={store.tier}
                    endorsements={store.endorsement_count}
                  />
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
                {creator && (
                  <span className="flex items-center gap-1">
                    by {creator.name}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Joined {new Date(store.created_at).toLocaleDateString()}
                </span>
                <span>{products.length} products</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              {user && creator && user.id !== creator.id && (
                <>
                  <Link to={`/messages/${creator.id}`}>
                    <Button className="btn-brutal" data-testid="message-creator-btn">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </Link>
                  
                  {user.is_verified && !hasEndorsed && (
                    <Dialog open={endorseDialogOpen} onOpenChange={setEndorseDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="btn-brutal" data-testid="endorse-btn">
                          <Heart className="w-4 h-4 mr-2" />
                          Endorse
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <Heart className="w-5 h-5 text-pastel-crochet" />
                            Endorse {store.name}
                          </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleEndorse} className="space-y-4 pt-4">
                          <p className="text-sm text-muted-foreground">
                            As a verified creator, your endorsement helps build trust in our community. Share why you recommend this store!
                          </p>
                          <Textarea
                            value={endorseMessage}
                            onChange={(e) => setEndorseMessage(e.target.value)}
                            placeholder="I love their work because..."
                            className="min-h-[100px]"
                          />
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setEndorseDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button type="submit" disabled={endorsing}>
                              {endorsing ? 'Endorsing...' : 'Add Endorsement'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {user.is_verified && hasEndorsed && (
                    <Button 
                      variant="outline" 
                      className="btn-brutal text-pastel-crochet"
                      onClick={handleRemoveEndorsement}
                    >
                      <Heart className="w-4 h-4 mr-2 fill-current" />
                      Endorsed
                    </Button>
                  )}
                  
                  {/* Shoutout Button - for verified creators to welcome new creators */}
                  {canShoutout && (
                    <Dialog open={shoutoutDialogOpen} onOpenChange={setShoutoutDialogOpen}>
                      <DialogTrigger asChild>
                        <Button 
                          className="btn-brutal bg-gradient-to-r from-pastel-crochet to-pastel-art hover:from-pastel-crochet/90 hover:to-pastel-art/90"
                          data-testid="shoutout-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Give Shoutout
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md">
                        <DialogHeader>
                          <DialogTitle className="flex items-center gap-2">
                            <span className="text-2xl">✨</span>
                            Welcome {creator?.name || 'this creator'}!
                            <span className="text-2xl">✨</span>
                          </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleShoutout} className="space-y-4 pt-4">
                          <div className="p-4 rounded-xl bg-gradient-to-r from-pastel-crochet/20 to-pastel-art/20 text-center">
                            <p className="text-sm">
                              {shoutoutInfo?.is_new_store 
                                ? `${creator?.name} joined ${shoutoutInfo?.days_old} days ago! Be one of the first to welcome them.`
                                : `Spread some warmth and welcome ${creator?.name} to the community!`
                              }
                            </p>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Your shoutout will be posted in the Community's "Welcome" channel for everyone to see!
                          </p>
                          <Textarea
                            value={shoutoutMessage}
                            onChange={(e) => setShoutoutMessage(e.target.value)}
                            placeholder={`Welcome to Gathered Things, ${creator?.name}! I'm so excited to see...`}
                            className="min-h-[120px]"
                            data-testid="shoutout-message-input"
                          />
                          <div className="flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={() => setShoutoutDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button 
                              type="submit" 
                              disabled={sendingShoutout}
                              className="bg-gradient-to-r from-pastel-crochet to-pastel-art"
                              data-testid="send-shoutout-btn"
                            >
                              {sendingShoutout ? 'Sending...' : 'Send Shoutout 🎉'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Endorsements Section */}
        {endorsements.length > 0 && (
          <div className="mb-8 card-brutal p-6">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-pastel-crochet" />
              Creator Endorsements ({endorsements.length})
            </h3>
            <div className="space-y-4">
              {endorsements.slice(0, 5).map((end) => (
                <div key={end.id} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50">
                  <div className="w-10 h-10 rounded-full bg-pastel-art/30 flex items-center justify-center font-bold flex-shrink-0">
                    {end.endorser_name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{end.endorser_name}</p>
                    {end.message && <p className="text-sm text-muted-foreground mt-1">{end.message}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products */}
        <div className="py-8">
          <h2 className="text-2xl font-heading font-bold mb-6">Products</h2>
          
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={{ ...product, store_name: store.name, creator_name: creator?.name, is_verified: store.is_verified, tier: store.tier, endorsement_count: store.endorsement_count }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-muted-foreground">
              <p>This store doesn't have any products yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
